import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { usePutUserTimezone } from '@/api/generated/user/user';
import type { LoginResponse } from '@/api/types/auth';
import { queryKeys } from '@/config/query/queryKeys';

type User = LoginResponse['user'];

function detectTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Silently aligns `User.timezone` with the user's machine timezone on every
 * authenticated load. Unlike locale (which we ask explicitly), timezone is a
 * fact about the device — auto-detect, no prompt, ever. PUT only when there's
 * a real change so we don't write on every focus.
 */
export function useTimezoneReconciliation() {
  const { data: user } = useQuery<User>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });

  const queryClient = useQueryClient();
  const updateTimezoneMutation = usePutUserTimezone();

  useEffect(() => {
    if (!user) {
      return;
    }
    if (updateTimezoneMutation.status !== 'idle') {
      return;
    }
    const detected = detectTimezone();
    if (!detected || detected === user.timezone) {
      return;
    }
    updateTimezoneMutation.mutate(
      { data: { timezone: detected } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
        },
      },
    );
  }, [user, queryClient, updateTimezoneMutation]);
}
